import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PlusCircle, Briefcase } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useAdminData } from '../hooks/useAdminData';
import { AdminSidebar } from './AdminSidebar';
import { UserManagementTable } from './UserManagementTable';
import { RolesManagementTable } from './RolesManagementTable';
import AuditLogPanel, { AuditLogHeaderActions } from './AuditLogPanel';
import AnalyticsPanel from './AnalyticsPanel';
import { ProposalReviewPanel } from './ProposalReviewPanel';
import { PortfolioManagement } from './PortfolioManagement';
import { TenantIntegrations } from './TenantIntegrations';
import { TenantJobFilters } from './TenantJobFilters';
import { TenantMessageFilters } from './TenantMessageFilters';
import { AIKnowledge } from './AIKnowledge';
import WorkflowSettings from './WorkflowSettings';
import { TenantRegisterPage } from './TenantRegisterPage';
import NotificationBell from './NotificationBell';
import { AccessDeniedCard } from './AccessDeniedCard';
import { ConfirmDialog } from './ConfirmDialog';
import { UserFormModal } from './UserFormModal';
import { InviteUserModal } from './InviteUserModal';
import { RoleFormModal } from './RoleFormModal';

const TAB_CONFIG = {
  users: { title: 'User Management', description: 'Manage team members and permissions' },
  roles: { title: 'Roles Management', description: 'Configure roles and access levels' },
  analytics: { title: 'Analytics', description: 'Track performance and insights' },
  proposals: { title: 'Proposal Reviews', description: 'Review and approve proposals' },
  portfolio: { title: 'Portfolio', description: 'Manage portfolio projects' },
  integrations: { title: 'Integrations & Credentials', description: 'Manage API credentials and integrations for your organization' },
  'job-filters': { title: 'Job Filters', description: 'Configure job filtering criteria' },
  'message-filters': { title: 'Message Filters', description: 'Configure message filtering criteria' },
  'vector-data': { title: 'AI Knowledge', description: 'Manage AI training data for better proposals' },
  'workflow': { title: 'Workflow Settings', description: 'Configure proposal review workflow' },
  'audit-logs': { title: 'Audit Logs', description: 'Track system activities' },
  'tenants/new': { title: 'Register Tenant', description: 'Create a new tenant organization' },
};

// Derive current tab key from pathname
const getActiveTab = (pathname) => {
  const match = pathname.replace(/^\/admin\/?/, '');
  return match || 'users';
};

export function AdminPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const {
    canAccessAdminPanel, canManageUsers, canManageRoles,
    canCreateUser, canReadUser, canUpdateUser, canDeleteUser, canInviteUser,
    canCreateRole, canUpdateRole, canDeleteRole, canReadInvitation,
    canReadAuditLog, canReadAnalytics, canReviewProposals,
    canReadPortfolio,
    canReadVectorData,
    canReadTenantConfig,
    canUpdateTenantConfig,
    canReadJobFilters,
    canUpdateJobFilters,
    canManageTenants,
  } = usePermissions();

  const data = useAdminData();

  const activeTab = getActiveTab(location.pathname);
  const [showAuditFilters, setShowAuditFilters] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('adminSidebarCollapsed');
    return stored ? stored === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Document title
  useEffect(() => {
    const tab = TAB_CONFIG[activeTab];
    document.title = tab ? `${tab.title} - Propel` : 'Admin Panel - Propel';
  }, [activeTab]);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'users') {
      if (canReadUser()) data.fetchUsers();
    } else if (activeTab === 'roles' && canManageRoles()) {
      data.fetchRoles();
    }
  }, [activeTab]);

  // Fetch roles on mount for user form dropdowns
  useEffect(() => {
    if (canManageRoles()) data.fetchRoles();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Access control
  if (!canAccessAdminPanel()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access the admin panel.</p>
          <button onClick={() => navigate('/')} className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const currentTab = TAB_CONFIG[activeTab] || TAB_CONFIG['users'];

  return (
    <div className="min-h-screen bg-gray-50 flex admin-compact overflow-hidden">
      <div className={`${isSidebarCollapsed ? 'w-14' : 'w-60'} fixed h-screen z-40`}>
        <AdminSidebar
          onLogout={handleLogout}
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed((prev) => !prev)}
        />
      </div>

      <div className={`flex-1 flex flex-col ${isSidebarCollapsed ? 'ml-14' : 'ml-60'}`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{currentTab.title}</h1>
                <p className="text-xs text-gray-500 mt-0.5">{currentTab.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                {activeTab === 'users' && (
                  <div className="flex items-center gap-2">
                    {canInviteUser() && (
                      <button
                        onClick={data.handleInviteUser}
                        className="px-2.5 py-1 bg-white text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors duration-150 flex items-center text-sm font-medium"
                        type="button"
                        data-onboarding="invite-user-button"
                      >
                        <PlusCircle className="mr-1.5 h-3.5 w-3.5 text-primary-600" />
                        Invite
                      </button>
                    )}
                    {canCreateUser() && (
                      <button onClick={data.handleCreateUser} className="px-2.5 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-150 flex items-center text-sm font-medium" type="button">
                        <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
                        Add User
                      </button>
                    )}
                  </div>
                )}
                {activeTab === 'roles' && canCreateRole() && (
                  <button onClick={() => data.setIsRoleModalOpen(true)} className="px-2.5 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-150 flex items-center text-sm font-medium" type="button">
                    <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
                    Add Role
                  </button>
                )}
                {activeTab === 'audit-logs' && canReadAuditLog() && (
                  <AuditLogHeaderActions onToggleFilters={() => setShowAuditFilters(!showAuditFilters)} showFilters={showAuditFilters} />
                )}
                <div className="h-5 w-px bg-gray-200" />
                <button onClick={() => navigate('/jobs')} className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors" type="button" title="View Jobs">
                  <Briefcase className="h-4 w-4" />
                </button>
                <NotificationBell />
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-y-auto min-h-0">
          {data.error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {data.error}
            </div>
          )}
          <Routes>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={
              <UserManagementTable
                users={data.users}
                roles={data.roles}
                onViewUser={(u) => data.openModal(u, 'view')}
                onEditUser={canUpdateUser() ? (u) => data.openModal(u, 'edit') : undefined}
                onDeleteUser={canDeleteUser() ? (userId) => openConfirmDialog(
                  'Delete User',
                  'Are you sure you want to delete this user? This action cannot be undone.',
                  () => data.handleDeleteUser(userId)
                ) : undefined}
                onSort={data.handleUsersSort}
                onFilter={data.handleUsersFilter}
                onSearch={data.handleSearchChange}
                onPageChange={data.handlePageChange}
                onPageSizeChange={data.handlePageSizeChange}
                currentPage={data.currentPage}
                totalPages={data.totalPages}
                totalCount={data.totalCount}
                pageSize={data.pageSize}
                loading={data.loading}
                canReadUser={canReadUser()}
                canUpdateUser={canUpdateUser()}
                canDeleteUser={canDeleteUser()}
              />
            } />
            <Route path="roles" element={
              !canManageRoles() ? (
                <AccessDeniedCard message="You don't have permission to manage roles." />
              ) : (
                <RolesManagementTable
                  roles={data.roles}
                  onEditRole={data.setEditRole}
                  onDeleteRole={(roleId) => openConfirmDialog(
                    'Delete Role',
                    'Are you sure you want to delete this role? This action cannot be undone.',
                    () => data.handleDeleteRole(roleId)
                  )}
                  onPageChange={data.handleRolesPageChange}
                  onPageSizeChange={data.handleRolesPageSizeChange}
                  currentPage={data.rolesCurrentPage}
                  totalPages={data.rolesTotalPages}
                  totalCount={data.rolesTotalCount}
                  pageSize={data.rolesPageSize}
                  loading={data.loading}
                  canUpdateRole={canUpdateRole()}
                  canDeleteRole={canDeleteRole()}
                />
              )
            } />
            <Route path="audit-logs" element={
              !canReadAuditLog() ? (
                <AccessDeniedCard message="You don't have permission to view audit logs." />
              ) : (
                <AuditLogPanel showFilters={showAuditFilters} onShowFiltersChange={setShowAuditFilters} />
              )
            } />
            <Route path="analytics" element={
              !canReadAnalytics() ? (
                <AccessDeniedCard message="You don't have permission to view analytics." />
              ) : (
                <AnalyticsPanel />
              )
            } />
            <Route path="proposals" element={
              !canReviewProposals() ? (
                <AccessDeniedCard message="You don't have permission to review proposals." />
              ) : (
                <ProposalReviewPanel />
              )
            } />
            <Route path="portfolio" element={
              !canReadPortfolio() ? (
                <AccessDeniedCard message="You don't have permission to manage portfolio projects." />
              ) : (
                <PortfolioManagement />
              )
            } />
            <Route path="integrations" element={
              !(canReadTenantConfig() || canUpdateTenantConfig()) ? (
                <AccessDeniedCard message="You don't have permission to view integrations." />
              ) : (
                <TenantIntegrations />
              )
            } />
            <Route path="job-filters" element={
              !(canReadJobFilters() || canUpdateJobFilters()) ? (
                <AccessDeniedCard message="You don't have permission to view job filters." />
              ) : (
                <TenantJobFilters />
              )
            } />
            <Route path="message-filters" element={
              !canUpdateTenantConfig() ? (
                <AccessDeniedCard message="You don't have permission to manage message filters." />
              ) : (
                <TenantMessageFilters />
              )
            } />
            <Route path="vector-data" element={
              !canReadVectorData() ? (
                <AccessDeniedCard message="You don't have permission to view AI knowledge." />
              ) : (
                <AIKnowledge />
              )
            } />
            <Route path="workflow" element={
              !canUpdateTenantConfig() ? (
                <AccessDeniedCard message="You don't have permission to update workflow settings." />
              ) : (
                <WorkflowSettings />
              )
            } />
            <Route path="tenants/new" element={
              !canManageTenants() ? (
                <AccessDeniedCard message="You don't have permission to create tenants." />
              ) : (
                <TenantRegisterPage />
              )
            } />
          </Routes>
        </main>
      </div>

      {/* Modals */}
      <UserFormModal
        isOpen={data.isModalOpen}
        onClose={data.closeModal}
        onSave={data.handleSaveUser}
        user={data.selectedUser}
        mode={data.modalMode}
        roles={data.roles}
        canManageRoles={canManageRoles()}
      />

      <InviteUserModal
        isOpen={data.isInviteModalOpen}
        onClose={data.closeInviteModal}
        onInvite={data.handleSendInvitation}
        roles={data.roles}
      />

      <RoleFormModal
        isOpen={data.isRoleModalOpen || !!data.editRole}
        onClose={() => { data.setIsRoleModalOpen(false); data.setEditRole(null); }}
        onSubmit={data.editRole ? data.handleUpdateRole : data.handleCreateRole}
        role={data.editRole}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </div>
  );
}

export default AdminPanel;
